/**
 * Where a page publishes, as editable chips.
 *
 * WHY CHIPS AND NOT A TEXT BOX. A path is a list, and every question a text box
 * raises — leading slash, trailing slash, a slash typed inside a name — is a
 * question a chip list never asks. Segments are also edited one at a time in
 * practice: people keep the branch and change the leaf, or drop one level.
 *
 * SUGGESTIONS ARE THE POINT, not decoration. Free text reliably produces
 * `types-of-ebikes` beside `type-of-ebikes` — two branches that read as one and
 * are two different URLs, invisible until someone reads the deployed site.
 * Offering what the site already uses turns the common case into a click.
 *
 * The value is always slugified, matching `_clean_path` on the server, so the
 * preview is the URL that ships rather than an optimistic version of it.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import {
  formatPath,
  matchSuggestions,
  slugifySegment,
  splitPasted,
} from './pathRules';

interface Props {
  segments: string[];
  onChange: (segments: string[]) => void;
  /** Scopes the suggestions. Without one there is nothing to suggest from. */
  managedSiteId: number | null;
  /** Shown under the chips. Omit to show the section path alone. */
  previewSlug?: string;
  /** False for flat targets (WordPress, Manual) — see pathRules.isNestedSiteType. */
  nested?: boolean;
  /** Reads "Seeded from <name>" so the prefill is explained, not just present. */
  seededFrom?: string;
  disabled?: boolean;
}

export default function PathChips({
  segments,
  onChange,
  managedSiteId,
  previewSlug,
  nested = true,
  seededFrom,
  disabled = false,
}: Props) {
  const [typed, setTyped] = useState('');
  const [pool, setPool] = useState<{ segment: string; page_count: number }[]>([]);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Suggestions are for the level being typed, so they refetch as the path
  // changes — the children of `bikes` are not the children of `bikes/trails`.
  //
  // Keyed on the JOINED path, not the array: an array prop is a new reference
  // on every parent render, and depending on identity would refetch on renders
  // where the path did not actually change.
  const pathKey = segments.join('/');
  useEffect(() => {
    let cancelled = false;
    if (!managedSiteId) {
      setPool([]);
      return;
    }
    (async () => {
      try {
        const res = await api.getPathSegments(
          managedSiteId,
          pathKey ? pathKey.split('/') : [],
        );
        if (!cancelled) setPool(res.suggestions);
      } catch {
        // A missing suggestion list is a smaller problem than a blocked
        // dialog: you can still type the path by hand.
        if (!cancelled) setPool([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [managedSiteId, pathKey]);

  const matches = useMemo(() => matchSuggestions(pool, typed), [pool, typed]);

  const commit = (raw: string) => {
    const next = splitPasted(raw);
    if (!next.length) return;
    onChange([...segments, ...next]);
    setTyped('');
    setOpen(false);
  };

  const removeAt = (i: number) => {
    onChange(segments.filter((_, idx) => idx !== i));
    inputRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === '/' || e.key === 'Tab') {
      if (typed.trim()) {
        e.preventDefault();
        commit(typed);
      }
      return;
    }
    // Backspace at an empty input removes the last chip — the standard
    // behaviour for a token field, and the fastest way to go up a level.
    if (e.key === 'Backspace' && !typed && segments.length) {
      e.preventDefault();
      removeAt(segments.length - 1);
    }
    if (e.key === 'Escape') setOpen(false);
  };

  const preview = formatPath(
    nested ? [...segments, previewSlug ?? ''] : [previewSlug ?? ''],
  );

  return (
    <div>
      <div
        className={`flex flex-wrap items-center gap-1 px-2 py-1.5 border rounded-md bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 ${
          disabled ? 'opacity-60' : 'focus-within:ring-1 focus-within:ring-blue-500'
        }`}
        onClick={() => inputRef.current?.focus()}
      >
        <span className="text-gray-400 dark:text-gray-500 text-sm select-none">/</span>
        {segments.map((seg, i) => (
          <span
            key={`${seg}-${i}`}
            className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded bg-blue-50 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 text-sm"
          >
            {seg}
            {!disabled && (
              <button
                type="button"
                aria-label={`Remove ${seg}`}
                onClick={(e) => {
                  e.stopPropagation();
                  removeAt(i);
                }}
                className="p-0.5 rounded hover:bg-blue-100 dark:hover:bg-blue-800 text-blue-500 dark:text-blue-300"
              >
                <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                </svg>
              </button>
            )}
            <span className="text-blue-300 dark:text-blue-500 select-none">/</span>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={typed}
          disabled={disabled}
          onChange={(e) => {
            setTyped(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            // Commit on blur rather than discarding — a segment typed and then
            // abandoned by clicking elsewhere is work, and Escape is how you
            // mean "no". Same rule as the label chips and the rail's node draft.
            if (typed.trim()) commit(typed);
            setOpen(false);
          }}
          onKeyDown={onKeyDown}
          onPaste={(e) => {
            const text = e.clipboardData.getData('text');
            if (text.includes('/')) {
              e.preventDefault();
              commit(text);
            }
          }}
          placeholder={segments.length ? 'add a level…' : 'site root'}
          aria-label="Add a path segment"
          className="flex-1 min-w-[8rem] bg-transparent text-sm outline-none dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
        />
      </div>

      {open && matches.length > 0 && (
        <ul className="mt-1 max-h-44 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 shadow-sm">
          {matches.map((m) => (
            <li key={m.segment}>
              <button
                type="button"
                // onMouseDown, not onClick: blur fires first and would commit
                // the half-typed text before the click ever landed.
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(m.segment);
                }}
                className="w-full flex items-center justify-between px-3 py-1.5 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-600 dark:text-gray-100"
              >
                <span>{m.segment}</span>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {m.page_count} page{m.page_count === 1 ? '' : 's'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
        {previewSlug !== undefined ? (
          <>
            Publishes at <code className="text-gray-700 dark:text-gray-300">{preview}</code>
          </>
        ) : (
          <>
            Path <code className="text-gray-700 dark:text-gray-300">{formatPath(segments)}</code>
          </>
        )}
        {!nested && ' - this site type publishes every page at the root, so the path is kept but not used in the URL.'}
        {nested && seededFrom && ` · seeded from ${seededFrom}`}
      </p>

      {typed && slugifySegment(typed) !== typed.trim().toLowerCase() && (
        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
          Will be saved as <code>{slugifySegment(typed) || '-'}</code>
        </p>
      )}
    </div>
  );
}
